import React from 'react';
import { useTranslation } from 'react-i18next';
import { shallow } from 'zustand/shallow';
import { ChevronUp, ShoppingCart } from '@carbon/react/icons';
import { useMoney } from 'src/hooks/useMoney';
import { usePOSStore } from 'src/states/pos';

interface CartBarProps {}

/**
 * Mobile-only summary of the current order, fixed to the bottom of the viewport.
 * Tapping it opens the full-screen cart sheet. The element is `display: none` above
 * the 768px breakpoint, so the desktop right-rail layout is untouched.
 */
const CartBar = React.memo((_: CartBarProps) => {
  const { t } = useTranslation('pos');
  const { format } = useMoney();
  const { currentOrderItems, cartSheetOpen, openCartSheet } = usePOSStore(
    (state) => ({
      currentOrderItems: state.currentOrderItems,
      cartSheetOpen: state.cartSheetOpen,
      openCartSheet: state.openCartSheet,
    }),
    shallow
  );

  if (currentOrderItems.length === 0 || cartSheetOpen) {
    return null;
  }

  const total = currentOrderItems.reduce((sum, item) => sum + item.subTotal, 0);

  return (
    <button type="button" className="pos-cart-bar" onClick={openCartSheet} aria-label={t('viewCurrentOrder')}>
      <ShoppingCart size={24} />
      <span className="pos-cart-bar__count">{currentOrderItems.length}</span>
      <span className="pos-cart-bar__label">{t('items')}</span>
      <span className="pos-cart-bar__total">{format(total)}</span>
      <ChevronUp size={20} />
    </button>
  );
});

export default CartBar;
